import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import { signal, computed, batch } from '@preact/signals'
import '@substrate-system/input'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import {
    ToolPage,
    Disclaimer,
    ToolWorkbench,
} from '../../components/tool-page.js'
import {
    calcChargeSize,
    type LineOd,
    type SystemType,
} from '../../util/charge-size.js'
import { REFRIGERANTS } from '../../data/refrigerants.js'

const REFRIGERANTS_WITH_DENSITY = REFRIGERANTS.filter(
    r => r.liquidDensityLbGal !== null
).slice(0, 9) as Array<typeof REFRIGERANTS[number] & {
    liquidDensityLbGal:number
}>

const LINE_OD_OPTIONS:LineOd[] = [
    '3/8', '1/2', '5/8', '3/4', '7/8', '1-1/8', '1-3/8', '1-5/8',
]

const SYSTEM_TYPE_LABELS:Record<SystemType, string> = {
    'residential-ac': 'Residential Split AC',
    'residential-heat-pump': 'Residential Split Heat Pump',
    'light-commercial-ac': 'Light Commercial AC (up to 5 tons)',
    'light-commercial-heat-pump': 'Light Commercial Heat Pump',
    'mini-split': 'Mini-Split / Ductless',
    'commercial-refrigeration': 'Commercial Refrigeration',
    'walk-in-cooler': 'Walk-in Cooler',
    'walk-in-freezer': 'Walk-in Freezer',
    'commercial-chiller': 'Commercial Chiller',
    other: 'Other',
}

const systemType = signal<SystemType>('residential-ac')
const refrigerantId = signal<string>('R-410A')
const factoryChargeLbs = signal<string>('')
const lineOd = signal<LineOd>('3/8')
const totalLineLengthFt = signal<string>('')
const verticalRiseFt = signal<string>('0')
const includedLengthFt = signal<string>('15')
const submitted = signal(false)
/* Bumped on reset to remount the substrate-input web components, which
   do not sync a programmatic value change back to their inner input. */
const formNonce = signal(0)

const result = computed(() => {
    if (!submitted.value) return null
    const factory = parseFloat(factoryChargeLbs.value)
    const total = parseFloat(totalLineLengthFt.value)
    const rise = parseFloat(verticalRiseFt.value)
    const included = parseFloat(includedLengthFt.value)
    if (isNaN(factory) || isNaN(total) || factory < 0 || total < 0) return null
    const ref = REFRIGERANTS_WITH_DENSITY.find(
        r => r.id === refrigerantId.value
    )
    if (!ref) return null
    return calcChargeSize({
        liquidDensityLbGal: ref.liquidDensityLbGal,
        lineOd: lineOd.value,
        totalLineLengthFt: total,
        verticalRiseFt: isNaN(rise) ? 0 : rise,
        factoryChargeLbs: factory,
        includedLengthFt: isNaN(included) ? 15 : included,
    })
})

function handleSubmit (e:Event) {
    e.preventDefault()
    submitted.value = true
}

function handleReset (e:Event) {
    /* substrate-button does not forward type="button", so the inner button
       defaults to submit. Cancel the implicit form submission here. */
    e.preventDefault()
    batch(() => {
        systemType.value = 'residential-ac'
        refrigerantId.value = 'R-410A'
        factoryChargeLbs.value = ''
        lineOd.value = '3/8'
        totalLineLengthFt.value = ''
        verticalRiseFt.value = '0'
        includedLengthFt.value = '15'
        submitted.value = false
        formNonce.value += 1
    })
}

export const ChargeSizeEstimator:FunctionComponent<{
    state:AppState
}> = function ChargeSizeEstimator () {
    const r = result.value
    const nonce = formNonce.value
    const selectedRef = REFRIGERANTS_WITH_DENSITY.find(
        ref => ref.id === refrigerantId.value
    )
    const isPhaseout = selectedRef &&
        (
            selectedRef.status === 'Phased Out' ||
            selectedRef.status === 'Phasing Down'
        )

    return html`
        <${ToolPage}
            title="Charge Size Estimator"
            description="Estimate total system refrigerant charge from equipment
                data and line-set length using the liquid-line-volume method.
                Verify final charge via superheat and subcooling readings."
        >
            <${ToolWorkbench}
                emptyHint="Choose the equipment and line-set details,
                    then select Calculate."
            >
                <form class="tool-form" onSubmit=${handleSubmit} novalidate>

                <div class="tool-form-section">
                    <p class="tool-section-label">Equipment</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="cse-system-type">
                                System Type
                            </label>
                            <select
                                id="cse-system-type"
                                class="tool-select"
                                value=${systemType.value}
                                onChange=${(e:Event) => {
                                    systemType.value =
                                        (e.target as HTMLSelectElement)
                                            .value as SystemType
                                }}
                            >
                                ${Object.entries(SYSTEM_TYPE_LABELS).map(
                                    ([val, label]) =>
                                        html`<option key=${val} value=${val}>
                                            ${label}
                                        </option>`
                                )}
                            </select>
                        </div>

                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="cse-refrigerant">
                                Refrigerant
                            </label>
                            <select
                                id="cse-refrigerant"
                                class="tool-select"
                                value=${refrigerantId.value}
                                onChange=${(e:Event) => {
                                    refrigerantId.value =
                                        (e.target as HTMLSelectElement).value
                                }}
                            >
                                ${REFRIGERANTS_WITH_DENSITY.map(ref =>
                                    html`<option key=${ref.id} value=${ref.id}>
                                        ${ref.name}
                                    </option>`
                                )}
                            </select>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`factoryChargeLbs-${nonce}`}
                                label="Factory Charge (lbs)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="0.1"
                                placeholder="e.g. 4.5"
                                value=${factoryChargeLbs.value}
                                onInput=${(e:Event) => {
                                    factoryChargeLbs.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Line Set</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="cse-line-od">
                                Liquid Line OD (inches)
                            </label>
                            <select
                                id="cse-line-od"
                                class="tool-select"
                                value=${lineOd.value}
                                onChange=${(e:Event) => {
                                    lineOd.value =
                                        (e.target as HTMLSelectElement)
                                            .value as LineOd
                                }}
                            >
                                ${LINE_OD_OPTIONS.map(od =>
                                    html`<option key=${od} value=${od}>
                                        ${od}"
                                    </option>`
                                )}
                            </select>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`totalLineLengthFt-${nonce}`}
                                label="Total Line Set Length (ft)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="1"
                                placeholder="e.g. 50"
                                value=${totalLineLengthFt.value}
                                onInput=${(e:Event) => {
                                    totalLineLengthFt.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`verticalRiseFt-${nonce}`}
                                label="Vertical Rise (ft)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="1"
                                placeholder="e.g. 10"
                                value=${verticalRiseFt.value}
                                onInput=${(e:Event) => {
                                    verticalRiseFt.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`includedLengthFt-${nonce}`}
                                label="Manufacturer Included Length (ft)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="1"
                                placeholder="Default: 15"
                                value=${includedLengthFt.value}
                                onInput=${(e:Event) => {
                                    includedLengthFt.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>
                    </div>
                </div>

                <div class="tool-actions">
                    <substrate-button class="primary" type="submit">
                        Calculate
                    </substrate-button>
                    <substrate-button
                        type="button"
                        onClick=${handleReset}
                    >
                        Reset
                    </substrate-button>
                </div>
            </form>

            ${r && html`
                <div class="tool-readout">
                    <p class="tool-readout-label">Estimated Total Charge</p>
                    <p class="tool-readout-value">
                        ${r.estimatedTotalChargeLbs.toFixed(2)}
                        <span class="tool-readout-unit">lbs</span>
                    </p>

                    <div class="tool-metrics">
                        <div class="tool-metric">
                            <p class="tool-metric-label">Factory Charge</p>
                            <p class="tool-metric-value">
                                ${r.factoryChargeLbs.toFixed(2)} lbs
                            </p>
                        </div>
                        <div class="tool-metric">
                            <p class="tool-metric-label">Line-Set Addition</p>
                            <p class="tool-metric-value">
                                ${r.lineSetAdditionLbs.toFixed(2)} lbs
                            </p>
                        </div>
                        <div class="tool-metric">
                            <p class="tool-metric-label">oz/ft (liquid line)</p>
                            <p class="tool-metric-value">
                                ${r.ouncesPerFoot.toFixed(2)} oz/ft
                            </p>
                        </div>
                    </div>

                    ${(r.longRunFlag || r.verticalRiseFlag || isPhaseout) &&
                        html`<aside class="tool-disclaimer" role="note">
                            ${r.longRunFlag && html`
                                <p class="tool-disclaimer-text">
                                    Long line set (&gt;50 ft): verify oil return
                                    and consider trap/loop requirements.
                                </p>
                            `}
                            ${r.verticalRiseFlag && html`
                                <p class="tool-disclaimer-text">
                                    Vertical rise &gt;15 ft: check manufacturer
                                    oil trap spacing requirements.
                                </p>
                            `}
                            ${isPhaseout && html`
                                <p class="tool-disclaimer-text">
                                    ${selectedRef!.name} is
                                    ${selectedRef!.status.toLowerCase()}.
                                    Consider approved alternatives:
                                    ${selectedRef!.approvedSubstitutes
                                        .join(', ')}.
                                </p>
                            `}
                        </aside>`
                    }

                    <p class="tool-readout-detail">
                        Values are estimates only. Always verify final charge
                        using superheat and subcooling readings per manufacturer
                        specifications.
                    </p>

                    <div class="tool-actions">
                        <substrate-button
                            type="button"
                            onClick=${handleReset}
                        >
                            Reset
                        </substrate-button>
                    </div>
                </div>
            `}
            </${ToolWorkbench}>

            <${Disclaimer} />
        </${ToolPage}>
    `
}
